import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import constants from '../../core/constants';
import { GameContext } from '../../core/context/game.context';
import { languagesCodes } from '../../core/entities';
import { configDataLayer, pushGtmDataLayer } from '../../core/helper/gtm.helper';
import { FacebookPixel } from '../../core/helper/third-parties-manager';
import { Challenge } from '../../core/interfaces/challenge.interface';
import { IInstanceInfo } from '../../core/interfaces/instaces.interfaces';
import { Languages } from '../../core/interfaces/misc.interface';
import { initAxiosInterceptors } from '../../core/rest/axios.config';
import { getChallenge, getChallenges } from '../../core/rest/challenge.service';
import GameService from '../../pages/game/GameService.component';
import { Landing } from '../../pages/landing/Landing.component';
import SummaryService from '../../pages/summary/SummaryService.component';
import Tutorial from '../../pages/tutorial/Tutorial.component';
import Spinner from '../spinner/Spinner.component';
import './instanceLoader.styles.scss';

export const InstanceLoader = () => {
  const [challengesState, setChallengesState] = useState<Challenge[]>([]);
  const [instancesInfoState, setInstancesInfoState] = useState<IInstanceInfo[]>([]);
  const location = useLocation();
  const [loadingState, setLoadingState] = useState(null);
  const [axiosReadyState, setAxiosReadyState] = useState(false);
  const gameCtx = useContext(GameContext);
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    configDataLayer(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    let [, lang, instanceId, , presetId] = location.pathname.split('/');
    let instance: IInstanceInfo;

    pushGtmDataLayer('pageView', location.pathname);
    if (instanceId && instancesInfoState.length) {
      instance = instancesInfoState.find((i) => i.id === instanceId);

      if (!instance) {
        instance = instancesInfoState.find((i) => i.name.search(instanceId) !== -1);
        if (instance) {
          navigate(`/${lang}/${instance.id}/${instance.name}`);
        }
      }
      if (instance) {
        instanceId = instance.id;
        gameCtx.setInstance(instance);
      }
    }
    if (presetId && gameCtx.getInstance()) {
      let preset = gameCtx.getInstance().presets.find((p) => p.id.toString() === presetId);

      if (!preset) {
        preset =
          gameCtx.getInstance().presets.find((p) => p.name.search(presetId) !== -1) || gameCtx.getInstance().presets[0];
        navigate(`/${lang}/${instanceId}/${instance.name}/${preset.id}/${preset.name}`);
      }
      gameCtx.setPresetId(preset.id);
    }
  }, [location, instancesInfoState]);

  useEffect(() => {
    const urlLng = languagesCodes.includes(location.pathname.split('/')[1])
      ? (location.pathname.split('/')[1] as Languages['language'])
      : null;
    const language = urlLng || gameCtx.getLanguage() || (navigator.language.split('-')[0] as Languages['language']);
    let mounted = true;

    FacebookPixel.init(constants.tp.fbPixelId, !!process.env.DEBUG);
    FacebookPixel.pageView();
    gameCtx.setLanguage(language);

    initAxiosInterceptors(() => gameCtx.getLanguage() || language)
      .then(() => {
        if (mounted) {
          setAxiosReadyState(true);
        }
      })
      .catch((error) => {
        console.error('Axios initialization failed', error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!axiosReadyState) {
      return;
    }

    getChallenges().then((data) => {
      setChallengesState(data);
    });
    i18n.changeLanguage(gameCtx.getLanguage());
  }, [axiosReadyState, gameCtx.getLanguage()]);

  useEffect(() => {
    if (challengesState.length) {
      setLoadingState(true);
      Promise.all(challengesState.map((challenge) => getChallenge(challenge.id))).then((data) => {
        const instances = data.map(mapChallengeToInstanceInfo);
        setInstancesInfoState(instances);
        gameCtx.setAllInstances(instances);
        setLoadingState(false);
      });
    }
  }, [gameCtx.getInstance()?.name, challengesState]);

  return (
    <section className="InstanceLoaderComponent">
      {loadingState ? <Spinner primaryColor="#7DDC55" secondaryColor="#FE6E5C" /> : null}
      <Routes>
        {instancesInfoState.length && loadingState === false && <Route path="*" element={<Navigate to="/" />} />}
        {instancesInfoState.length && loadingState === false && (
          <Route path="/" element={<Navigate to={gameCtx.getLanguage()} />} />
        )}
        {gameCtx.getLanguage() && (
          // /lgn language selector route
          <Route path={gameCtx.getLanguage()}>
            {/* /lng -> landing component */}
            <Route path="" element={<Landing instancesInfos={instancesInfoState} />} />
            {
              instancesInfoState.map((instance) => (
                // /lng/instance-id
                <Route key={instance.id} path={instance.id}>
                  {/* // /lng/instance-id -> navigate to instance-name */}
                  <Route
                    path=""
                    element={<Navigate to={`/${gameCtx.getLanguage()}/${instance.id}/${instance.name}`} />}
                  />
                  {/* fallback */}
                  <Route path="*" element={<Navigate to={`/${gameCtx.getLanguage()}/${instance.id}`} />} />
                  {/* /lng/instance-id/instance-name */}
                  <Route path={instance.name}>
                    {/* /lng/instance-id/instance-name -> tutorial*/}
                    <Route path="" element={<Tutorial />} />
                    {/* fallback */}
                    <Route
                      path="*"
                      element={<Navigate to={`/${gameCtx.getLanguage()}/${instance.id}/${instance.name}`} />}
                    />
                    {/* /lng/instance-id/instance-name/preset-id */}
                    {instance?.presets.map((p) => (
                      <Route key={p.id} path={p.id.toString()}>
                        {/* /lng/instance-id/instance-name/preset-id -> /lng/instance-name/preset-id/preset-name */}
                        <Route
                          path=""
                          element={
                            <Navigate
                              to={`/${gameCtx.getLanguage()}/${instance.id}/${instance.name}/${p.id}/${p.name}`}
                            />
                          }
                        />
                        {/* fallback */}
                        <Route
                          path="*"
                          element={
                            <Navigate to={`/${gameCtx.getLanguage()}/${instance.id}/${instance.name}/${p.id}`} />
                          }
                        />
                        {/* /lng/instance-id/instance-name/preset-id/preset-name -> tutorial comp */}
                        <Route path={p.name} element={<Tutorial />} />
                      </Route>
                    ))}
                    {/* /lng/instance-id/instance-name/preset-id/preset-name/game -> game comp */}
                    {instance?.presets.map((p) => (
                      <Route key={p.id} path={`${p.id}/${p.name}/game`} element={<GameService />} />
                    ))}
                    {/* /lng/instance-id/instance-name/preset-id/preset-name/summary -> summary comp */}
                    {instance?.presets.map((p) => (
                      <Route key={p.id} path={`${p.id}/${p.name}/summary`} element={<SummaryService />} />
                    ))}
                  </Route>
                </Route>
              ))
            }
            {!!instancesInfoState.length && <Route path="*" element={<Navigate to={`/${gameCtx.getLanguage()}`} />} />}
          </Route>
        )}
      </Routes>
    </section>
  );
};

function mapChallengeToInstanceInfo(challenge: Challenge): IInstanceInfo {
  const routeName = toRouteSegment(challenge.name || challenge.title || challenge.id);

  return {
    id: challenge.id,
    challenge,
    configurations: {
      id: 0,
      bgQuantity: 1,
    },
    presets: [
      {
        id: challenge.id,
        maxErrorsPerQuestion: 0,
        maxQuestions: challenge.number_questions,
        maxWrongQuestions: challenge.max_errors,
        options: challenge.number_answers,
        skipTO: challenge.skip_loquis_time_sec,
        stepScore: 1,
        userResponseTO: challenge.response_time_sec,
        name: routeName,
        title: challenge.title,
        subtitle: challenge.description,
        tagline: challenge.subtitle,
        cardImageUrl: challenge.picture_url_square,
        background: challenge.picture_url_background,
        logo: challenge.picture_url_horizontal,
        publicName: challenge.name,
      },
    ],
    labelsSet: {
      id: 0,
      tutorial: {
        title: challenge.title,
        subtitle: challenge.description,
        tagline: challenge.subtitle || '',
      },
      summary: {
        taglines: [challenge.share_text || ''],
        share: challenge.share_text || '',
      },
      presets: [{ id: challenge.id, name: challenge.name, presetId: challenge.id }],
    },
    emailTplId: 0,
    language: challenge.language,
    name: routeName,
  };
}

function toRouteSegment(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
